import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : '/api';

// Helper function to extract error message
const extractErrorMessage = (error, defaultMessage = 'Une erreur est survenue') => {
  const detail = error?.response?.data?.detail;
  if (Array.isArray(detail)) {
    const firstError = detail[0];
    return firstError?.msg || firstError?.message || defaultMessage;
  } else if (typeof detail === 'object' && detail !== null) {
    return detail.msg || detail.message || defaultMessage;
  } else if (typeof detail === 'string') {
    return detail;
  }
  return error?.message || defaultMessage;
};

// Plan Delete Confirmation Modal (internal component)
const PlanDeleteConfirmModal = ({ isOpen, onClose, onConfirm, planName }) => {
  if (!isOpen) return null;

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999
      }}
      onClick={onClose}
      data-testid="plan-delete-modal"
    >
      <div 
        style={{
          backgroundColor: '#2C3148',
          borderRadius: '12px',
          padding: '24px',
          maxWidth: '400px',
          width: '90%',
          boxShadow: '0 20px 60px rgba(0,0,0,0.8)',
          border: '2px solid #3A4359'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{
          color: '#FFFFFF',
          fontSize: '18px',
          fontWeight: '600',
          marginBottom: '16px'
        }}>
          Confirmer la suppression
        </h3>
        
        <p style={{
          color: '#B8C0E0',
          fontSize: '14px',
          marginBottom: '20px',
          lineHeight: '1.5'
        }}>
          Voulez-vous vraiment supprimer le plan <strong style={{color: '#00C2FF'}}>{planName}</strong> ?
        </p>

        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              backgroundColor: '#1C2133',
              color: '#8A92B2',
              border: '1px solid #3A4359',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#242A3D';
              e.currentTarget.style.color = '#FFFFFF';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#1C2133';
              e.currentTarget.style.color = '#8A92B2';
            }}
            data-testid="plan-delete-cancel-btn"
          >
            Annuler
          </button>
          <button
            onClick={() => {
              onConfirm();
              onClose();
            }}
            style={{
              padding: '10px 20px',
              backgroundColor: 'rgba(255, 87, 87, 0.15)',
              color: '#FF8A8A',
              border: '1px solid rgba(255, 87, 87, 0.3)',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255, 87, 87, 0.25)';
              e.currentTarget.style.borderColor = '#FF5757';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255, 87, 87, 0.15)';
              e.currentTarget.style.borderColor = 'rgba(255, 87, 87, 0.3)';
            }}
            data-testid="plan-delete-confirm-btn"
          >
            Supprimer
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * User Edit Modal Component
 * Allows admin to edit user details and manage cemetery plans
 */
const UserEditModal = ({ isOpen, onClose, onUserUpdated, editingUser, setMessageModal }) => {
  const [email, setEmail] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [originalAccessCode, setOriginalAccessCode] = useState('');
  const [cityHallAddress, setCityHallAddress] = useState('');
  const [mayorName, setMayorName] = useState('');
  const [secretaryName, setSecretaryName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [role, setRole] = useState('client');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasChangedPassword, setHasChangedPassword] = useState(false);
  
  // Cemetery plan states
  const [cemeteryPlan, setCemeteryPlan] = useState(null);
  const [planUploading, setPlanUploading] = useState(false);
  const [planPreview, setPlanPreview] = useState(null);
  const [showDeletePlanModal, setShowDeletePlanModal] = useState(false);

  // Cleanup object URL when component unmounts or preview changes
  useEffect(() => {
    return () => {
      if (planPreview && planPreview.startsWith('blob:')) {
        URL.revokeObjectURL(planPreview);
      }
    };
  }, [planPreview]);

  // Populate form with editing user data
  useEffect(() => {
    if (editingUser && isOpen) {
      setEmail(editingUser.email || '');
      const code = editingUser.access_code || '';
      const userHasChangedPassword = editingUser.is_first_login === false;
      
      if (userHasChangedPassword) {
        setAccessCode('');
        setOriginalAccessCode('');
      } else {
        setAccessCode(code);
        setOriginalAccessCode(code);
      }
      
      setCityHallAddress(editingUser.city_hall_address || '');
      setMayorName(editingUser.mayor_name || '');
      setSecretaryName(editingUser.secretary_name || '');
      setPhoneNumber(editingUser.phone_number || '');
      setRole(editingUser.role || 'client');
      setHasChangedPassword(userHasChangedPassword);
      
      fetchCemeteryPlan();
    }
  }, [editingUser, isOpen]);

  const fetchCemeteryPlan = async () => {
    if (!editingUser) return;
    
    if (planPreview && planPreview.startsWith('blob:')) {
      URL.revokeObjectURL(planPreview);
    }
    
    try {
      const response = await axios.get(`${API}/admin/users/${editingUser.id}/cemetery-plan`);
      setCemeteryPlan(response.data);
      if (response.data) {
        const imageResponse = await axios.get(`${API}/admin/users/${editingUser.id}/cemetery-plan/file`, {
          responseType: 'blob',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        });
        
        const imageUrl = URL.createObjectURL(imageResponse.data);
        setPlanPreview(imageUrl);
      } else {
        setPlanPreview(null);
      }
    } catch (err) {
      if (err.response?.status !== 404) {
        console.error('Error fetching cemetery plan:', err);
      }
      setCemeteryPlan(null);
      setPlanPreview(null);
    }
  };

  const generateAccessCode = async () => {
    try {
      const response = await axios.get(`${API}/admin/generate-access-code`);
      setAccessCode(response.data.access_code);
    } catch (err) {
      console.error('Error generating access code:', err);
      const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
      let result = '';
      for (let i = 0; i < 12; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      setAccessCode(result);
    }
  };

  const handlePlanUpload = async (file) => {
    if (!editingUser) return;

    const allowedExtensions = ['.jpg', '.jpeg', '.png'];
    const fileExt = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    
    if (!allowedExtensions.includes(fileExt)) {
      setMessageModal({
        isOpen: true,
        title: 'Format non autorisé',
        message: 'Format de fichier non autorisé. Formats acceptés : .jpg, .png',
        type: 'error'
      });
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      setMessageModal({
        isOpen: true,
        title: 'Fichier trop volumineux',
        message: 'Fichier trop volumineux (max 50MB)',
        type: 'error'
      });
      return;
    }

    setPlanUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(`${API}/admin/users/${editingUser.id}/cemetery-plan`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      await new Promise(resolve => setTimeout(resolve, 500));
      await fetchCemeteryPlan();
    } catch (err) {
      console.error('Error uploading cemetery plan:', err);
      setMessageModal({
        isOpen: true,
        title: 'Erreur',
        message: extractErrorMessage(err, 'Erreur lors du téléchargement du plan'),
        type: 'error'
      });
    } finally {
      setPlanUploading(false);
    }
  };

  const handlePlanDelete = async () => {
    if (!editingUser || !cemeteryPlan) return;

    try {
      await axios.delete(`${API}/admin/users/${editingUser.id}/cemetery-plan`);
      setCemeteryPlan(null);
      setPlanPreview(null);
    } catch (err) {
      console.error('Error deleting cemetery plan:', err);
      setMessageModal({
        isOpen: true,
        title: 'Erreur',
        message: 'Erreur lors de la suppression du plan',
        type: 'error'
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const userData = {
        email: email.trim(),
        role,
        city_hall_address: cityHallAddress.trim() || null,
        mayor_name: mayorName.trim() || null,
        secretary_name: secretaryName.trim() || null,
        phone_number: phoneNumber.trim() || null
      };

      if (accessCode.trim() !== originalAccessCode) {
        userData.access_code = accessCode.trim();
      }

      const response = await axios.put(`${API}/admin/users/${editingUser.id}`, userData);
      
      onUserUpdated(response.data);
      onClose();
    } catch (err) {
      setError(extractErrorMessage(err, 'Erreur lors de la modification de l\'utilisateur'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setAccessCode('');
    setCityHallAddress('');
    setMayorName('');
    setSecretaryName('');
    setPhoneNumber('');
    setRole('client');
    setError('');
    onClose();
  };

  if (!isOpen || !editingUser) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50" style={{backgroundColor: 'rgba(0, 0, 0, 0.8)'}} data-testid="user-edit-modal">
      <div className="rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto shadow-2xl" style={{backgroundColor: '#2C3148'}}>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold" style={{color: '#FFFFFF'}}>Éditer l&apos;utilisateur</h2>
          <button
            onClick={handleClose}
            className="transition-colors duration-200"
            style={{color: '#8A92B2'}}
            onMouseEnter={(e) => e.currentTarget.style.color = '#FF5757'}
            onMouseLeave={(e) => e.currentTarget.style.color = '#8A92B2'}
            data-testid="user-edit-close-btn"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="user-edit-form">
          {/* Email */}
          <div>
            <label className="block text-sm font-medium mb-1" style={{color: '#FFFFFF'}}>
              Email <span style={{color: '#FF5757'}}>*</span>
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
              style={{ backgroundColor: '#242A3D', borderColor: '#4A5BFF', color: '#FFFFFF' }}
              placeholder="user@example.com"
              data-testid="user-edit-email"
            />
          </div>

          {/* Access Code */}
          <div>
            <label className="block text-sm font-medium mb-1" style={{color: '#FFFFFF'}}>
              Code d&apos;accès {!hasChangedPassword && <span style={{color: '#FF5757'}}>*</span>}
            </label>
            
            {hasChangedPassword && (
              <div className="mb-2 p-2 border rounded text-sm" style={{
                backgroundColor: 'rgba(0, 194, 255, 0.1)',
                borderColor: '#00C2FF',
                color: '#00C2FF'
              }}>
                ✓ L&apos;utilisateur a modifié son mot de passe.
              </div>
            )}
            
            <div className="flex space-x-2">
              <input
                type="text"
                required={!hasChangedPassword}
                value={accessCode}
                onChange={(e) => setAccessCode(e.target.value)}
                className="flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
                style={{ backgroundColor: '#242A3D', borderColor: '#4A5BFF', color: '#FFFFFF' }}
                placeholder={hasChangedPassword ? "Générer un nouveau code" : "Code d'accès"}
                data-testid="user-edit-access-code"
              />
              <button
                type="button"
                onClick={generateAccessCode}
                className="px-3 py-2 rounded-md focus:outline-none transition-colors duration-200"
                style={{ backgroundColor: '#1C2133', color: '#00C2FF' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#242A3D'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#1C2133'}
                title="Générer un nouveau code"
                data-testid="user-edit-generate-code-btn"
              >
                🔄
              </button>
            </div>
            
            {hasChangedPassword && (
              <p className="mt-1 text-xs" style={{color: '#8A92B2'}}>
                💡 Générer un nouveau code réinitialisera le mot de passe.
              </p>
            )}
          </div>

          {/* Role */}
          <div>
            <label className="block text-sm font-medium mb-1" style={{color: '#FFFFFF'}}>Rôle</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
              style={{ backgroundColor: '#242A3D', borderColor: '#4A5BFF', color: '#FFFFFF' }}
              data-testid="user-edit-role"
            >
              <option value="client">Client</option>
              <option value="demo">Demo (accès limité)</option>
              <option value="admin">Admin</option>
            </select>
            {role === 'demo' && (
              <p className="text-xs mt-1" style={{color: '#FFB800'}}>
                ⚠️ Le compte demo peut visualiser mais ne peut pas enregistrer de modifications
              </p>
            )}
          </div>

          {/* City Hall Address */}
          <div>
            <label className="block text-sm font-medium mb-1" style={{color: '#FFFFFF'}}>Adresse Mairie</label>
            <input
              type="text"
              value={cityHallAddress}
              onChange={(e) => setCityHallAddress(e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
              style={{ backgroundColor: '#242A3D', borderColor: '#4A5BFF', color: '#FFFFFF' }}
              placeholder="123 Rue de la Mairie, Ville"
              data-testid="user-edit-city-hall"
            />
          </div>

          {/* Mayor Name */}
          <div>
            <label className="block text-sm font-medium mb-1" style={{color: '#FFFFFF'}}>Nom du Maire</label>
            <input
              type="text"
              value={mayorName}
              onChange={(e) => setMayorName(e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
              style={{ backgroundColor: '#242A3D', borderColor: '#4A5BFF', color: '#FFFFFF' }}
              placeholder="Jean Dupont"
              data-testid="user-edit-mayor"
            />
          </div>

          {/* Secretary Name */}
          <div>
            <label className="block text-sm font-medium mb-1" style={{color: '#FFFFFF'}}>Nom de la secrétaire</label>
            <input
              type="text"
              value={secretaryName}
              onChange={(e) => setSecretaryName(e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
              style={{ backgroundColor: '#242A3D', borderColor: '#4A5BFF', color: '#FFFFFF' }}
              placeholder="Marie Martin"
              data-testid="user-edit-secretary"
            />
          </div>

          {/* Phone Number */}
          <div>
            <label className="block text-sm font-medium mb-1" style={{color: '#FFFFFF'}}>Numéro de téléphone</label>
            <input
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
              style={{ backgroundColor: '#242A3D', borderColor: '#4A5BFF', color: '#FFFFFF' }}
              placeholder="01 23 45 67 89"
              data-testid="user-edit-phone"
            />
          </div>

          {/* Cemetery Plan Section */}
          <div style={{
            marginTop: '20px',
            padding: '16px',
            backgroundColor: '#1A1E2E',
            borderRadius: '8px',
            border: '1px solid #3A4359'
          }}>
            <h3 className="text-lg font-semibold mb-3" style={{color: '#00C2FF'}}>
              📍 Plan du cimetière
            </h3>

            {cemeteryPlan ? (
              <div>
                <div style={{
                  backgroundColor: '#242A3D',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '12px',
                  border: '1px solid #4A5BFF'
                }}>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p style={{color: '#B8C0E0', fontSize: '14px', fontWeight: '600'}}>
                        {cemeteryPlan.original_filename}
                      </p>
                      <p style={{color: '#8A92B2', fontSize: '12px', marginTop: '4px'}}>
                        {(cemeteryPlan.file_size / 1024 / 1024).toFixed(2)} MB • 
                        Mis à jour le {new Date(cemeteryPlan.updated_at).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                  </div>

                  {/* Preview */}
                  <div style={{
                    width: '100%',
                    height: '200px',
                    backgroundColor: '#0F1419',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    marginBottom: '12px',
                    border: '1px solid #3A4359'
                  }}>
                    <img
                      src={planPreview}
                      alt="Aperçu du plan"
                      style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                    />
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <a
                      href={planPreview}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        flex: 1,
                        display: 'inline-block',
                        textAlign: 'center',
                        padding: '8px 12px',
                        backgroundColor: 'rgba(0, 194, 255, 0.15)',
                        color: '#00C2FF',
                        border: '1px solid rgba(0, 194, 255, 0.3)',
                        borderRadius: '6px',
                        textDecoration: 'none',
                        fontSize: '13px',
                        fontWeight: '600'
                      }}
                      data-testid="user-edit-view-plan-btn"
                    >
                      👁️ Voir
                    </a>
                    <button
                      type="button"
                      onClick={() => setShowDeletePlanModal(true)}
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        backgroundColor: 'rgba(255, 87, 87, 0.15)',
                        color: '#FF8A8A',
                        border: '1px solid rgba(255, 87, 87, 0.3)',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                      data-testid="user-edit-delete-plan-btn"
                    >
                      🗑️ Supprimer
                    </button>
                  </div>
                </div>

                <label style={{
                  display: 'inline-block',
                  padding: '10px 16px',
                  backgroundColor: 'rgba(0, 194, 255, 0.1)',
                  color: '#00C2FF',
                  border: '1px solid rgba(0, 194, 255, 0.2)',
                  borderRadius: '6px',
                  cursor: planUploading ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  opacity: planUploading ? 0.6 : 1
                }}>
                  {planUploading ? '⏳ Remplacement...' : '🔄 Remplacer le plan'}
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    disabled={planUploading}
                    style={{display: 'none'}}
                    onChange={(e) => {
                      if (e.target.files[0]) {
                        handlePlanUpload(e.target.files[0]);
                        e.target.value = '';
                      }
                    }}
                  />
                </label>
              </div>
            ) : (
              <div>
                <p style={{color: '#8A92B2', fontSize: '14px', marginBottom: '12px'}}>
                  Aucun plan n&apos;a été uploadé pour cet utilisateur
                </p>
                <label style={{
                  display: 'inline-block',
                  padding: '10px 16px',
                  backgroundColor: 'rgba(0, 194, 255, 0.15)',
                  color: '#00C2FF',
                  border: '1px solid rgba(0, 194, 255, 0.3)',
                  borderRadius: '6px',
                  cursor: planUploading ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  opacity: planUploading ? 0.6 : 1
                }}>
                  {planUploading ? '⏳ Téléchargement...' : '📤 Télécharger un plan'}
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    disabled={planUploading}
                    style={{display: 'none'}}
                    onChange={(e) => {
                      if (e.target.files[0]) {
                        handlePlanUpload(e.target.files[0]);
                        e.target.value = '';
                      }
                    }}
                  />
                </label>
                <p style={{color: '#5B638C', fontSize: '12px', marginTop: '8px'}}>
                  Formats acceptés : .jpg, .png (max 50MB)
                </p>
              </div>
            )}
          </div>

          {error && (
            <div className="text-sm p-3 rounded-md" style={{
              color: '#FF5757',
              backgroundColor: 'rgba(255, 87, 87, 0.1)',
              border: '1px solid #FF5757'
            }} data-testid="user-edit-error">
              {error}
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 border rounded-md transition-colors duration-200"
              style={{ color: '#8A92B2', borderColor: '#1C2133' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#1C2133';
                e.currentTarget.style.color = '#FFFFFF';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#8A92B2';
              }}
              data-testid="user-edit-cancel-btn"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 rounded-md transition-all duration-300 disabled:opacity-50"
              style={{
                background: 'linear-gradient(135deg, #4A5BFF 0%, #7A5FFF 100%)',
                color: '#FFFFFF'
              }}
              onMouseEnter={(e) => {
                if (!isLoading) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 8px 20px rgba(74, 91, 255, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '';
              }}
              data-testid="user-edit-submit-btn"
            >
              {isLoading ? 'Modification...' : 'Modifier'}
            </button>
          </div>
        </form>
      </div>

      <PlanDeleteConfirmModal
        isOpen={showDeletePlanModal}
        onClose={() => setShowDeletePlanModal(false)}
        onConfirm={handlePlanDelete}
        planName={cemeteryPlan?.original_filename || ''}
      />
    </div>
  );
};

export default UserEditModal;
